import React, { useState, useEffect } from "react";

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    quantity: "",
    referenceFile: null as File | null,
    acceptance: false
  });

  const [showPrivacyNotification, setShowPrivacyNotification] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'file') {
      const fileInput = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: fileInput.files ? fileInput.files[0] : null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    }

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) errors.name = "Name is required";

    // Enhanced email validation
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Enhanced phone validation (10-15 digits, allows +, spaces, hyphens)
    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else {
      const phoneDigits = formData.phone.replace(/[\s\-+()]/g, '');
      if (!/^\d{10,15}$/.test(phoneDigits)) {
        errors.phone = "Please enter a valid phone number (10-15 digits)";
      }
    }

    if (!formData.message.trim()) errors.message = "Message is required";

    // Enhanced quantity validation
    if (!formData.quantity.trim()) {
      errors.quantity = "Quantity is required";
    } else if (!/^\d+$/.test(formData.quantity.trim())) {
      errors.quantity = "Please enter a valid number";
    }

    if (!formData.acceptance) errors.acceptance = "You must agree to data collection";
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.acceptance) {
      setShowPrivacyNotification(true);
      return;
    }
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSubmitting(true);
    setSubmitError("");
    setShowErrorNotification(false);
    setShowSuccessNotification(false);

    try {
      const apiUrl: any = process.env.REACT_APP_CONTACT_API_URL;

      const response = await fetch(`${apiUrl}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          quantity: formData.quantity,
          message: formData.message,
          from: "Modli 3D Contact Form"
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status === "error") {
        throw new Error(result.message || "There was an error trying to send your message. Please try again.");
      }

      // Show success notification
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 5000);

      setFormData({
        name: "", email: "", phone: "", message: "", quantity: "", referenceFile: null, acceptance: false
      });
      setFormErrors({});
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "An error occurred.");
      setShowErrorNotification(true);
      setTimeout(() => setShowErrorNotification(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (formData.acceptance && showPrivacyNotification) {
      setShowPrivacyNotification(false);
    }
  }, [formData.acceptance, showPrivacyNotification]);

  return (
    <>
      <section
        id="contact"
        className="elementor-section elementor-top-section elementor-element elementor-element-3dfc49c elementor-section-full_width elementor-section-height-default elementor-section-height-default sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
        data-id="3dfc49c"
        data-element_type="section"
        style={{ position: "relative", overflow: "hidden", minHeight: "700px" }}
      >
        {/* Google Map iframe as background */}
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d5543.2832008550895!2d80.15775783671417!3d12.911558243952374!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a525ee602bff21d%3A0x8092acf620c1fe00!2sJothi%20Software%20Pvt.%20Ltd.!5e0!3m2!1sen!2sin!4v1712235819368!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0, position: "absolute", top: 0, left: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Google Maps - Jothi Software Pvt. Ltd. Location"
        ></iframe>

        {/* Content wrapper over the map */}
        <div
          className="elementor-container elementor-column-gap-no"
          style={{ position: "relative", zIndex: 1, top: '320px', padding: "40px 20px" }}
        >
          <div
            className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-5692abf sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
            data-id="5692abf"
            data-element_type="column"
          >
            <div className="elementor-widget-wrap elementor-element-populated">
              <section
                style={{ marginTop: '0px' }}
                className="elementor-section elementor-inner-section elementor-element elementor-element-1057f35 form-position-abs elementor-section-boxed elementor-section-height-default elementor-section-height-default sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                data-id="1057f35"
                data-element_type="section"
              >
                <div className="elementor-container elementor-column-gap-no">
                  {/* Left Column */}
                  <div
                    className="elementor-column elementor-col-40 elementor-inner-column elementor-element elementor-element-93db54d sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                    data-id="93db54d"
                    data-element_type="column"
                  >
                    <div className="elementor-widget-wrap">
                      {/* Optional content can go here */}
                    </div>
                  </div>

                  {/* Right Column with Title & Form */}
                  <div
                    className="elementor-column elementor-col-60 elementor-inner-column elementor-element elementor-element-c5b94a8 scheme_default sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                    data-id="c5b94a8"
                    data-element_type="column"
                    data-settings='{"background_background":"classic"}'
                  >
                    <div style={{ marginBottom: '0px' }} className="elementor-widget-wrap elementor-element-populated">
                      {/* Title */}
                      <div
                        className="elementor-element elementor-element-1015028 sc_fly_static elementor-widget elementor-widget-trx_sc_title trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                        data-id="1015028"
                        data-element_type="widget"
                        data-widget_type="trx_sc_title.default"
                      >
                        <div className="elementor-widget-container">
                          <div className="sc_title sc_title_default">
                            <span className="sc_item_subtitle sc_title_subtitle sc_item_subtitle_above sc_item_title_style_default">
                              Contact us
                            </span>
                            <h2 className="sc_item_title sc_title_title sc_item_title_style_default">
                              <span className="sc_item_title_text">
                                Have questions? <br />Get in touch!
                              </span>
                            </h2>
                          </div>
                        </div>
                      </div>

                      {/* Spacer */}
                      <div
                        className="elementor-element elementor-element-5f7e6d1 sc_height_tiny sc_fly_static elementor-widget elementor-widget-spacer trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                        data-id="5f7e6d1"
                        data-element_type="widget"
                        data-widget_type="spacer.default"
                      >
                        <div className="elementor-widget-container">
                          <div className="elementor-spacer">
                            <div className="elementor-spacer-inner"></div>
                          </div>
                        </div>
                      </div>

                      {/* Contact Form */}
                      <div
                        className="elementor-element elementor-element-ed54f34 sc_fly_static elementor-widget elementor-widget-trx_sc_contact_form_7"
                        data-element_type="widget"
                      >
                        <div className="elementor-widget-container">
                          <style>{`
                      @keyframes slideInUp {
                        from {
                          transform: translateY(100%);
                          opacity: 0;
                        }
                        to {
                          transform: translateY(0);
                          opacity: 1;
                        }
                      }
                      .form-error { color: #ff6b35; font-size: 12px; margin-top: 5px; display: block; }
                      .form-field-error { border-color: #ff6b35 !important; }
                      .submit-error { color: #ff6b35; font-size: 14px; margin-top: 10px; text-align: center; }
                      .style-line::before { font-family: 'trx_addons_icons'; position: absolute; left: 5px; top: 12px; color: #EE5536; font-size: 16px; z-index: 1; }
                      .style-line.icon-name::before { content: '\\e9ae'; }
                      .style-line.icon-email::before { content: '\\e99e'; }
                      .style-line.icon-phone::before { content: '\\e9a8'; }
                      .style-line.icon-subject::before { content: '\\e9b2'; }
                      .style-line.icon-textarea::before { content: '\\e9ac'; top: 15px; }
                      .style-line input, .style-line textarea { padding-left: 45px !important; color: #1B1E25 !important; }
                      .wpcf7-form-control::placeholder { color: #b2b2b2 !important; opacity: 1; font-weight: 300; }
                      
                      @media (max-width: 767px) {
                        #contact {
                          display: flex;
                          flex-direction: column;
                          min-height: auto !important;
                        }
                        #contact iframe {
                          position: relative !important;
                          height: 400px !important;
                          order: 1;
                        }
                        #contact .elementor-container {
                          position: relative !important;
                          top: 0 !important;
                          order: 2;
                          padding: 20px !important;
                        }
                        #contact .elementor-col-40,
                        #contact .elementor-col-60 {
                          width: 100% !important;
                          max-width: 100% !important;
                        }
                        #contact .elementor-inner-section {
                          width: 100% !important;
                        }
                        #contact .elementor-container.elementor-column-gap-no {
                          flex-direction: column;
                        }
                      }
                    `}</style>
                          {showPrivacyNotification && (
                            <div style={{
                              position: 'fixed', bottom: '20px', right: '20px', backgroundColor: '#ee5536', color: 'white',
                              padding: '15px 20px', borderRadius: '8px', zIndex: 9999, fontSize: '14px', animation: 'slideInUp 0.3s ease-out'
                            }}>
                              Please accept the terms of our Privacy Policy.
                            </div>
                          )}

                          {/* Success Notification */}
                          {showSuccessNotification && (
                            <div style={{
                              position: 'fixed', bottom: '20px', right: '20px', backgroundColor: '#10b981', color: 'white',
                              padding: '20px 24px', borderRadius: '12px', boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
                              zIndex: 9999, fontSize: '14px', fontWeight: '500', animation: 'slideInUp 0.3s ease-out', maxWidth: '400px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '24px' }}>✅</span>
                                <div>
                                  <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '4px' }}>Thank you!</div>
                                  <div style={{ fontSize: '13px', opacity: 0.95 }}>We've received your request and will get back to you shortly.</div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Error Notification */}
                          {showErrorNotification && (
                            <div style={{
                              position: 'fixed', bottom: '20px', right: '20px', backgroundColor: '#ef4444', color: 'white',
                              padding: '20px 24px', borderRadius: '12px', boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)',
                              zIndex: 9999, fontSize: '14px', fontWeight: '500', animation: 'slideInUp 0.3s ease-out', maxWidth: '400px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '24px' }}>⚠️</span>
                                <div>
                                  <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '4px' }}>Something went wrong.</div>
                                  <div style={{ fontSize: '13px', opacity: 0.95 }}>Please try again or contact us directly.</div>
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="wpcf7 js alert_inited" id="wpcf7-f1324-p1453-o1" lang="en-US" dir="ltr">
                            <form onSubmit={handleSubmit} className="wpcf7-form init" aria-label="Contact form" noValidate>
                              <fieldset className="hidden-fields-container" style={{ border: 'none', padding: 0, margin: 0 }}>
                                <input type="hidden" name="_wpcf7" value="1324" />
                                <input type="hidden" name="_wpcf7_version" value="6.1" />
                                <input type="hidden" name="_wpcf7_locale" value="en_US" />
                                <input type="hidden" name="_wpcf7_unit_tag" value="wpcf7-f1324-p1453-o1" />
                                <input type="hidden" name="_wpcf7_container_post" value="1453" />
                                <input type="hidden" name="_wpcf7_posted_data_hash" value="" />
                              </fieldset>
                              <div className="form-style-3">
                                <div className="columns_wrap">
                                  <div className="column-1_2">
                                    <p>
                                      <span className="style-line icon-name">
                                        <span className="wpcf7-form-control-wrap" data-name="your-name">
                                          <input
                                            size={40}
                                            maxLength={400}
                                            className={`wpcf7-form-control wpcf7-text wpcf7-validates-as-required fill_inited ${formErrors.name ? 'form-field-error' : ''}`}
                                            aria-required="true"
                                            aria-invalid={formErrors.name ? "true" : "false"}
                                            placeholder="Your full name"
                                            value={formData.name}
                                            type="text"
                                            name="name"
                                            onChange={handleInputChange}
                                          />
                                          <span className="line"></span>
                                        </span>
                                        {formErrors.name && <span className="form-error">{formErrors.name}</span>}
                                      </span>
                                    </p>
                                  </div>
                                  <div className="column-1_2">
                                    <p>
                                      <span className="style-line icon-email">
                                        <span className="wpcf7-form-control-wrap" data-name="your-email">
                                          <input
                                            size={40}
                                            maxLength={400}
                                            className={`wpcf7-form-control wpcf7-email wpcf7-validates-as-required wpcf7-text wpcf7-validates-as-email fill_inited ${formErrors.email ? 'form-field-error' : ''}`}
                                            aria-required="true"
                                            aria-invalid={formErrors.email ? "true" : "false"}
                                            placeholder="you@example.com"
                                            value={formData.email}
                                            type="email"
                                            name="email"
                                            onChange={handleInputChange}
                                          />
                                          <span className="line"></span>
                                        </span>
                                        {formErrors.email && <span className="form-error">{formErrors.email}</span>}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                                <div className="columns_wrap">
                                  <div className="column-1_2">
                                    <p>
                                      <span className="style-line icon-phone">
                                        <span className="wpcf7-form-control-wrap" data-name="phone">
                                          <input
                                            size={40}
                                            maxLength={400}
                                            className={`wpcf7-form-control wpcf7-tel wpcf7-validates-as-required wpcf7-text wpcf7-validates-as-tel fill_inited ${formErrors.phone ? 'form-field-error' : ''}`}
                                            aria-required="true"
                                            aria-invalid={formErrors.phone ? "true" : "false"}
                                            placeholder="Country code + phone number"
                                            value={formData.phone}
                                            type="tel"
                                            name="phone"
                                            onChange={handleInputChange}
                                          />
                                          <span className="line"></span>
                                        </span>
                                        {formErrors.phone && <span className="form-error">{formErrors.phone}</span>}
                                      </span>
                                    </p>
                                  </div>
                                  <div className="column-1_2">
                                    <p>
                                      <span className="style-line icon-subject">
                                        <span className="wpcf7-form-control-wrap" data-name="quantity">
                                          <input
                                            size={40}
                                            maxLength={400}
                                            className={`wpcf7-form-control wpcf7-text wpcf7-validates-as-required fill_inited ${formErrors.quantity ? 'form-field-error' : ''}`}
                                            aria-required="true"
                                            aria-invalid={formErrors.quantity ? "true" : "false"}
                                            placeholder="Quantity (e.g., 1, 5, 20, 100)"
                                            value={formData.quantity}
                                            type="text"
                                            name="quantity"
                                            onChange={handleInputChange}
                                          />
                                          <span className="line"></span>
                                        </span>
                                        {formErrors.quantity && <span className="form-error">{formErrors.quantity}</span>}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                                <div className="columns_wrap">
                                  <div className="column-1_1">
                                    <p>
                                      <span className="style-line icon-textarea">
                                        <span className="wpcf7-form-control-wrap" data-name="your-message">
                                          <textarea
                                            cols={40}
                                            rows={10}
                                            maxLength={2000}
                                            className={`wpcf7-form-control wpcf7-textarea wpcf7-validates-as-required fill_inited ${formErrors.message ? 'form-field-error' : ''}`}
                                            aria-required="true"
                                            aria-invalid={formErrors.message ? "true" : "false"}
                                            placeholder="Describe what you want to create. Include size, purpose, or any details you already have."
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            style={{ height: '100px' }}
                                          ></textarea>
                                          <span className="line"></span>
                                        </span>
                                        {formErrors.message && <span className="form-error">{formErrors.message}</span>}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                                <p>
                                  <span className="wpcf7-submit-style">
                                    <input
                                      className="wpcf7-form-control wpcf7-submit has-spinner"
                                      type="submit"
                                      value={isSubmitting ? "Sending..." : "Send Enquiry"}
                                      disabled={isSubmitting}
                                    />
                                    <span className="submit-style-in"></span>
                                  </span>
                                  <span className="wpcf7-spinner"></span>
                                  <span className="wpcf7-form-control-wrap" data-name="acceptance">
                                    <span className="wpcf7-form-control wpcf7-acceptance">
                                      <span className="wpcf7-list-item">
                                        <label>
                                          <input
                                            type="checkbox"
                                            name="acceptance"
                                            checked={formData.acceptance}
                                            onChange={handleInputChange}
                                            aria-invalid={formErrors.acceptance ? "true" : "false"}
                                            className={`inited ${formErrors.acceptance ? 'form-field-error' : ''}`}
                                          />
                                          <span className="wpcf7-list-item-label" style={{ fontSize: '13px', marginLeft: '8px' }}>
                                            I agree that my data is collected
                                          </span>
                                        </label>
                                        {formErrors.acceptance && <span className="form-error">{formErrors.acceptance}</span>}
                                      </span>
                                    </span>
                                  </span>
                                  {submitError && <div className="submit-error">{submitError}</div>}
                                </p>
                              </div>
                              <div className="wpcf7-response-output" aria-hidden="true"></div>
                            </form>
                          </div>
                        </div>
                      </div>
                      {/* End of Contact Form */}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>


    </>
  );
};

export default ContactSection;